import { Suspense } from 'react';

import { useGetTabularFlexiDashboardData } from './useGetTabularFlexiDashboardData';
import { TTopUpStatus } from '../../../../dataHelpers/GetTopUpStatus/getTopUpStatus.types';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { TProductType } from '../../../dashboard/Dashboard.types';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { PromotionAccordion } from '../../../promotion/PromotionAccordion';
import { SpillBanner } from '../../../spill/SpillBanner';
import * as selector from '../../accountsSelector';
import { AwaitingFundsView } from '../../awaitingFundsView/AwaitingFundsView';
import EndedView from '../../EndedView/EndedView';
import NewOutstandingView from '../../NewOutstandingView/NewOutstandingView';
import NewOverdueView from '../../NewOverdueView/NewOverdueView';
import UnknownView from '../../UnknownView/UnknownView';

export const TabularFlexiDashboard = ({ accountId }: { accountId: string }) => {
    const { status, productType, topUpStatus } =
        useGetTabularFlexiDashboardData(accountId);
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                        data-testId="loan-overview-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <div
                    data-testid="multi-loan-dashboard"
                    className="col-span-full"
                >
                    <DashboardView
                        status={status!}
                        accountId={accountId}
                        productType={productType!}
                        topUpStatus={topUpStatus}
                    />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const DashboardView = ({
    status,
    accountId,
    productType,
    topUpStatus
}: {
    status: string;
    accountId: string;
    productType: TProductType;
    topUpStatus: TTopUpStatus | undefined;
}) => {
    switch (status) {
        case selector.OUTSTANDING_STATUS:
            return (
                <>
                    <SpillBanner />

                    {topUpStatus !== 'TOPUP_ELIGIBLE' && (
                        <div className="mt-l">
                            <PromotionAccordion />
                        </div>
                    )}

                    <NewOutstandingView
                        accountId={accountId}
                        productType={productType}
                    />
                </>
            );

        case selector.OVERDUE_STATUS:
            return (
                <>
                    <SpillBanner />

                    <NewOverdueView
                        accountId={accountId}
                        productType={productType}
                    />
                </>
            );
        case selector.ENDED_STATUS:
            return (
                <>
                    <SpillBanner />
                    <EndedView
                        accountId={accountId}
                        productType={productType}
                    />
                </>
            );
        case selector.AWAITING_FUNDS_STATUS:
        case selector.TOPUP_STATUS:
            return <AwaitingFundsView />;

        case selector.UNKNOWN_STATUS:
        default:
            return (
                <UnknownView
                    accountId={accountId}
                    productType={productType}
                    accountStatus={status}
                />
            );
    }
};
